/**
 * ExpertiseSelfRatingPanel.tsx
 *
 * Profile setup & self-rating wizard component.
 * Allows users to self-declare skills, set 0–10 sliders, add interest tags,
 * and view a gamified profile completeness checklist.
 */

import React, { useState } from 'react';
import type { SpecialisationSelfRating } from '../../identity/domain/profile-claims.js';
import { SEED_TAXONOMY_V1 } from '../../identity/domain/taxonomy.js';
import { getCategoryVectorIcon } from './UserAvatar.js';
import {
  AwardIcon,
  SparklesIcon,
  CheckCircleIcon,
  CheckIcon,
} from '../icons.js';

export interface ExpertiseSelfRatingPanelProps {
  readonly initialSkills?: readonly SpecialisationSelfRating[];
  readonly initialInterests?: readonly string[];
  readonly completenessScore?: number;
  readonly onSave: (skills: SpecialisationSelfRating[], interests: string[]) => void;
  readonly onOpenPhotoEditor?: () => void;
}

const CATEGORIES = SEED_TAXONOMY_V1.categories;

export const ExpertiseSelfRatingPanel: React.FC<ExpertiseSelfRatingPanelProps> = ({
  initialSkills = [],
  initialInterests = [],
  completenessScore = 0,
  onSave,
  onOpenPhotoEditor,
}) => {
  const [selectedSkills, setSelectedSkills] = useState<Map<string, SpecialisationSelfRating>>(
    new Map(initialSkills.map((s) => [s.categoryId, s])),
  );
  const [interests, setInterests] = useState<string[]>([...initialInterests]);
  const [newInterestInput, setNewInterestInput] = useState<string>('');

  const toggleCategory = (categoryId: string) => {
    const next = new Map(selectedSkills);
    if (next.has(categoryId)) {
      next.delete(categoryId);
    } else {
      next.set(categoryId, {
        categoryId,
        selfRating: 5,
        updatedAt: new Date().toISOString(),
      });
    }
    setSelectedSkills(next);
  };

  const updateRating = (categoryId: string, rating: number) => {
    const next = new Map(selectedSkills);
    const existing = next.get(categoryId);
    if (existing) {
      next.set(categoryId, {
        ...existing,
        selfRating: rating,
        updatedAt: new Date().toISOString(),
      });
      setSelectedSkills(next);
    }
  };

  const handleSave = () => {
    onSave(Array.from(selectedSkills.values()), interests);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', color: '#f8fafc' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>1. Select Your Specialties</h4>
        {onOpenPhotoEditor && (
          <button
            type="button"
            onClick={onOpenPhotoEditor}
            style={{
              padding: '4px 10px',
              backgroundColor: '#1e293b',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              color: '#38bdf8',
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            Edit Photo
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
        {CATEGORIES.map((cat) => {
          const isSelected = selectedSkills.has(cat.id);
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => toggleCategory(cat.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 10px',
                borderRadius: '8px',
                border: isSelected ? '1px solid #38bdf8' : '1px solid rgba(255, 255, 255, 0.08)',
                backgroundColor: isSelected ? 'rgba(56, 189, 248, 0.12)' : '#1e293b',
                color: isSelected ? '#38bdf8' : '#94a3b8',
                fontSize: '12px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              {getCategoryVectorIcon(cat.id, 14, isSelected ? '#38bdf8' : '#94a3b8')}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.label}</span>
            </button>
          );
        })}
      </div>

      {selectedSkills.size > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>2. Rate Your Experience (0 to 10)</h4>
          {Array.from(selectedSkills.values()).map((skill) => {
            const cat = CATEGORIES.find((c) => c.id === skill.categoryId);
            return (
              <div
                key={skill.categoryId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: '#1e293b',
                  padding: '10px 14px',
                  borderRadius: '8px',
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: 500, minWidth: '100px' }}>{cat?.label || skill.categoryId}</span>
                <input
                  type="range"
                  min={0}
                  max={10}
                  value={skill.selfRating}
                  onChange={(e) => updateRating(skill.categoryId, parseInt(e.target.value, 10))}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#38bdf8', minWidth: '24px' }}>
                  {skill.selfRating}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={handleSave}
        style={{
          marginTop: '8px',
          padding: '10px 16px',
          backgroundColor: '#3b82f6',
          border: 'none',
          borderRadius: '8px',
          color: '#ffffff',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Save Expertise Claims
      </button>
    </div>
  );
};
